import { MentaError } from './errors';
import { FieldsController } from './fields';
import { InstallmentsClient, resolveApiBaseUrl } from './installments';
import { SiteStatusCheck } from './site-status';
import { sharedMessageRouter } from './transport/message-router';
import type { FieldStyle, FieldStyleByState, IframeTheme } from './transport/iframe';

export type { FieldStyle, FieldStyleByState };

/** Validates the build-time `VITE_SECURE_FIELDS_ORIGIN` constant: non-empty
 * string AND a well-formed absolute URL, matching the real
 * `sdk.mercadopago.com/js/v2` two-tier empty/invalid validation pattern
 * (spec, resolved). Both error messages name the variable so a caller
 * staring at a thrown `MentaError` knows which build input to check. */
function validateOrigin(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new MentaError('invalid_options', 'VITE_SECURE_FIELDS_ORIGIN can not be null/empty');
  }
  try {
    new URL(value);
  } catch {
    throw new MentaError('invalid_options', 'invalid VITE_SECURE_FIELDS_ORIGIN');
  }
  return value;
}

/** `siteKey` is required on every `new Menta(...)` call (S4): missing,
 * non-string, or blank throws synchronously with `invalid_options`, naming
 * `siteKey`. There is no prefix rule — site keys are per site, not per
 * environment (big-bang rename, S4 — no dual support with the old,
 * `pk_`-prefixed option). */
function validateSiteKey(value: unknown): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new MentaError('invalid_options', 'siteKey must be a non-empty string');
  }
  return value;
}

/** Options accepted after the required `siteKey` — `new Menta(siteKey,
 * options)`, mirroring `new MercadoPago(publicKey, options)`. `locale`/
 * `theme`/`style` are DEFAULTS for every field created via
 * `menta.fields.create()`; a per-field option of the same name always wins
 * (Engram odd/sdk-options/tasks, item 1/5). */
export interface MentaConstructorOptions {
  locale?: string;
  theme?: IframeTheme;
  style?: FieldStyleByState;
}

/**
 * `Menta` — the SDK's public entry point (`new Menta(siteKey, options?)` ->
 * `.fields` -> `FieldsController`). Constructor/instance pattern (not a
 * global mutable singleton) per the proposal's MP/Stripe.js rationale. This
 * SDK is not in production yet, so the constructor has exactly ONE
 * signature — no legacy `new Menta({ siteKey })` object-form overload is
 * kept.
 *
 * `menta.fields.create()` is the ONLY public per-field API surface for
 * this feature: there is no legacy `mount()` wrapper. NO backward
 * compatibility is provided for the pre-split combined-iframe `mount()` API
 * (design D6 was dropped — see engram
 * `sdd/split-secure-fields-per-input/decision-no-legacy`).
 */
export class Menta {
  private readonly secureFieldsOrigin: string;
  private readonly siteKey: string;
  private readonly defaultLocale: string | undefined;
  private readonly defaultTheme: IframeTheme | undefined;
  private readonly defaultStyle: FieldStyleByState | undefined;
  private readonly siteStatus: SiteStatusCheck;
  private readonly installments: InstallmentsClient;
  private fieldsController: FieldsController | undefined;
  /** Set once by `destroy()` (Engram odd/sdk-options/tasks, item 4). After
   * this, `.fields` keeps returning the same (now-destroyed) controller
   * rather than lazily minting a new one — `FieldsController` itself is
   * what throws on `create()`/`createCardToken()` once destroyed. */
  private destroyed = false;

  constructor(siteKey: string, options?: MentaConstructorOptions) {
    this.siteKey = validateSiteKey(siteKey);
    this.defaultLocale = options?.locale;
    this.defaultTheme = options?.theme;
    this.defaultStyle = options?.style;
    // Read here, not at module scope: Vite inlines the literal wherever the
    // `import.meta.env.*` member expression appears, so this stays build-time
    // while also letting `vi.stubEnv` drive it under Vitest.
    this.secureFieldsOrigin = validateOrigin(import.meta.env.VITE_SECURE_FIELDS_ORIGIN);
    // Advisory pre-check, started once; never throws and never blocks the
    // constructor (see docs/protocol.md, "Site status").
    this.siteStatus = new SiteStatusCheck(this.secureFieldsOrigin, this.siteKey);
    // Same build-time read, but a missing/invalid API URL does NOT throw: it
    // only disables the PAN `installments` event (one console.warn when a
    // listener subscribes), so `new Menta()` keeps working for merchants that
    // never use installments.
    this.installments = new InstallmentsClient(resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL), this.siteKey);
  }

  /** Lazily constructs exactly one `FieldsController` (and therefore one
   * field group / `groupId`) per `Menta` instance, on first access. */
  get fields(): FieldsController {
    if (!this.fieldsController) {
      this.fieldsController = new FieldsController(
        this.secureFieldsOrigin,
        sharedMessageRouter,
        this.siteKey,
        { locale: this.defaultLocale, theme: this.defaultTheme, style: this.defaultStyle },
        this.siteStatus,
        this.installments,
      );
    }
    return this.fieldsController;
  }

  /** Unmounts every field + the coordinator iframe and removes all
   * listeners (Engram odd/sdk-options/tasks, item 4). Idempotent. After
   * this, `fields.create()`/`fields.createCardToken()` throw
   * `MentaError('destroyed', ...)` — a destroyed `Menta` instance can never
   * be revived; the merchant page must construct a new one. */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.fieldsController?.destroy();
  }
}
